import * as React from 'react'
import withHydrationOnDemand from 'react-hydration-on-demand'

import { useSiteConfig } from '@thg-commerce/enterprise-core'
import {
  GlobalHelpCentreAccordion,
  GlobalHelpCentreAccordion2,
  GlobalHelpCentreAccordionCollection,
  GlobalHelpCentreCollection,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import {
  AccordionContainerProps,
  HorizontalAlignment,
} from '@thg-commerce/enterprise-widget-accordion-container'
import {
  HelpCentreCollection,
  HelpCentreCollectionProps,
} from '@thg-commerce/enterprise-widget-help-centre-collection'
import { TrustBox } from '@thg-commerce/enterprise-widget-trustpilot'
import { mq, spacing, styled } from '@thg-commerce/gravity-theme'

import { BaseWidgetProps } from '../types'

interface HelpCentreAccordionCollection
  extends Omit<
    GlobalHelpCentreAccordionCollection,
    'id' | 'query' | 'banners'
  > {
  banners:
    | Omit<GlobalHelpCentreAccordion, 'id' | 'query'>[]
    | Omit<GlobalHelpCentreAccordion2, 'id' | 'query'>[]
}

interface HelpCentreCollectionWithChildren
  extends Omit<GlobalHelpCentreCollection, 'id' | 'query' | 'banners'> {
  banners: HelpCentreAccordionCollection[]
}

export type HelpCentreCollectionWidgetRendererProps = BaseWidgetProps &
  Omit<HelpCentreCollectionWithChildren, 'query'>

const StyledContainer = styled.div`
  background: ${(props) => props.theme.colors.palette.greys.lighter};
`

const StyledHelpCentreCollection = styled(HelpCentreCollection)`
  padding: ${spacing(3)} 0 ${spacing(1)};
  margin: 0 auto;
  max-width: ${(props) => props.theme.site.siteWidth};

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    padding: ${spacing(4)} ${spacing(4)} ${spacing(10)};
  }
`

export const TrustPilotContainer = styled.div`
  padding: ${spacing(7)};
  background-color: ${(props) => props.theme.colors.palette.greys.white};
`

const TrustBoxComponent = withHydrationOnDemand({
  on: ['visible'],
})(TrustBox)

export const HelpCentreCollectionWidgetRenderer = (
  props: HelpCentreCollectionWidgetRendererProps,
) => {
  const { trustPilot, enableHelpCentreTrustPilot } = useSiteConfig()

  if (props?.banners?.length === 0) {
    return null
  }

  const helpCentreCollectionWidgetProps: HelpCentreCollectionProps = {
    accordions: props.banners.reduce<HelpCentreCollectionProps['accordions']>(
      (accumulator, accordion) => {
        if ((accordion.banners || []).length === 0) {
          return accumulator
        }
        accumulator.push({
          title: accordion.title || '',
          items: accordion.banners.map((accordionItem) => {
            return {
              title: accordionItem.accordionHeading,
              content: accordionItem.accordionContent?.content[0]?.content,
              isOpen: false,
            }
          }),
          alignment: HorizontalAlignment.LEFT,
        })
        return accumulator
      },
      [],
    ),
  }

  const TrustPilot = () =>
    enableHelpCentreTrustPilot &&
    (trustPilot?.enableTrustBox || trustPilot?.enableTrustPilot) ? (
      <TrustPilotContainer>
        <TrustBoxComponent {...trustPilot} />
      </TrustPilotContainer>
    ) : null

  return (
    <StyledContainer>
      <StyledHelpCentreCollection {...helpCentreCollectionWidgetProps} />
      <TrustPilot />
    </StyledContainer>
  )
}
